import { apiRequest, apiRequestBlob } from '@/lib/httpClient'
import {
  SAMPLE_EXPORTS,
  SAMPLE_REPORT,
  SampleCallError,
  isSampleCall,
} from './sampleCall'
import type {
  AdvisoryAgendaTemplate,
  AdvisoryAiExportFormat,
  AdvisoryAiExportEnqueueResponse,
  AdvisoryAiExportStatus,
  AdvisoryAiThemeColors,
  AdvisoryExport,
  AdvisoryAiOrgFilters,
  AdvisoryAiOrgInternalMemory,
  AdvisoryAiOrgProfile,
  AdvisoryAiOrgReportRow,
  AdvisoryAiOrgReportsResponse,
  AdvisoryAiOrgRow,
  IndustryProfileStatus,
  AdvisoryAiReportDetail,
  AdvisoryAiReportType,
  AnalyzeAdvisoryReportInput,
  BatchUpdateFindingsInput,
  CreateAdvisoryReportInput,
  CreateAdvisoryReportResponse,
  CreateAdvisoryAgendaTemplateInput,
  DashboardCard,
  DeepDiveChatRequest,
  DeepDiveChatResponse,
  GenerateAdvisoryAiReportInput,
  GenerateAdvisoryAiReportResponse,
  MappingRow,
  SurveyFormRow,
  UpdateAdvisoryAgendaTemplateInput,
  UpdateAdvisoryAiOrgProfileInput,
  UpdateAdvisoryAiOrgInternalMemoryInput,
  UpdateAdvisoryAiThemeColorsInput,
  UpdateAdvisoryCallInput,
} from './types'
import { mockDelay } from '@/api/mockDelay'
import { asArray, pickBool, pickNumber, pickString } from '@/api/shared/coercion'
import { platformTypeLabelFromTypeId } from '@/features/advisory-ai/org-list/constants'

const refreshStatusByPlatformStatusId: Record<number, string> = {
  0: 'Pending',
  1: 'Pending',
  2: 'Refreshing',
  3: 'Refreshed',
  4: 'Failed',
  5: 'Connected',
}

/**
 * Accepts either shape the grid may grow: a boolean flag, or the memory text
 * itself. Absent entirely means the API change has not landed, which reads as
 * 'unknown' so the consolidated modal stays open rather than blocking everyone.
 */
function readIndustryProfileStatus(row: Record<string, unknown>): IndustryProfileStatus {
  const flag = row['hasInternalMemory'] ?? row['HasInternalMemory']
  if (typeof flag === 'boolean') return flag ? 'filled' : 'empty'

  const text = row['internalMemory'] ?? row['InternalMemory']
  if (typeof text === 'string') return text.trim() ? 'filled' : 'empty'

  return 'unknown'
}

function mapPlatformGridRowToAdvisoryAiOrg(row: Record<string, unknown>): AdvisoryAiOrgRow {
  const platformId = pickString(row['platformId'] ?? row['PlatformId'])
  const id = pickString(row['id'] ?? row['Id']) || platformId
  const typeId = pickNumber(row['typeId'] ?? row['TypeId'])
  const statusId = pickNumber(row['statusId'] ?? row['StatusId'])
  const rawLsr = row['lastSuccessfulRefresh'] ?? row['LastSuccessfulRefresh']
  const lastSuccessfulRefresh =
    rawLsr == null || rawLsr === '' ? null : typeof rawLsr === 'string' ? rawLsr : String(rawLsr)
  const existingLabel = pickString(row['platformTypeLabel'] ?? row['PlatformTypeLabel'])
  return {
    id,
    companyId: pickString(row['companyId'] ?? row['CompanyId']) || id,
    name: pickString(row['name'] ?? row['Name']),
    companyName: pickString(row['companyName'] ?? row['CompanyName']),
    typeId,
    platformTypeLabel: existingLabel || platformTypeLabelFromTypeId(typeId),
    refreshStatus: refreshStatusByPlatformStatusId[statusId] ?? 'Pending',
    lastSuccessfulRefresh,
    industryProfileStatus: readIndustryProfileStatus(row),
  }
}

export type {
  AdvisoryAccountMapping,
  AdvisoryAgendaItem,
  AdvisoryAgendaTemplate,
  AdvisoryAgendaTemplateLine,
  AdvisoryAiExportFormat,
  AdvisoryAiExportEnqueueResponse,
  AdvisoryAiThemeColors,
  AdvisoryExport,
  AdvisoryAiOrgFilters,
  AdvisoryAiOrgInternalMemory,
  AdvisoryAiOrgProfile,
  AdvisoryAiOrgReportRow,
  AdvisoryAiOrgReportsResponse,
  AdvisoryAiOrgRow,
  IndustryProfileStatus,
  AdvisoryAiReportDetail,
  AdvisoryAiReportType,
  AnalyzeAdvisoryReportInput,
  CreateAdvisoryReportInput,
  CreateAdvisoryReportResponse,
  CreateAdvisoryAgendaTemplateInput,
  DashboardCard,
  DeepDiveChatRequest,
  DeepDiveChatResponse,
  DeepDiveChatMessage,
  DeepDiveChatResponseMessage,
  GenerateAdvisoryAiReportInput,
  GenerateAdvisoryAiReportResponse,
  MappingRow,
  SurveyFormRow,
  UpdateAdvisoryAgendaTemplateInput,
  UpdateAdvisoryAiOrgProfileInput,
  UpdateAdvisoryAiOrgInternalMemoryInput,
  UpdateAdvisoryAiThemeColorsInput,
  UpdateAdvisoryCallInput,
} from './types'

export type AdvisoryStatus = 'Active' | 'Pending' | 'Failed'

const mappingRows: MappingRow[] = Array.from({ length: 18 }, (_, i) => ({
  id: `map-${i + 1}`,
  name: `Mapping ${i + 1}`,
  platform: (['Xero', 'MYOB', 'QuickBooks'] as const)[i % 3] as MappingRow['platform'],
  status: (['Active', 'Pending', 'Failed'] as const)[i % 3] as AdvisoryStatus,
  updatedAt: `2026-02-${String((i % 20) + 1).padStart(2, '0')}`,
}))

const surveyRows: SurveyFormRow[] = Array.from({ length: 14 }, (_, i) => ({
  id: `srv-${i + 1}`,
  name: `Survey Form ${i + 1}`,
  responses: (i % 11) + 3,
  status: (['Active', 'Pending', 'Failed'] as const)[i % 3] as AdvisoryStatus,
  updatedAt: `2026-02-${String((i % 20) + 1).padStart(2, '0')}`,
}))

function formatDateForApi(value: Date | string): string {
  if (value instanceof Date) {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseDate(value: unknown): Date {
  const dateString = pickString(value)
  if (!dateString) return new Date()
  const date = new Date(dateString)
  return Number.isNaN(date.getTime()) ? new Date() : date
}

export async function fetchAdvisoryDashboardCards(): Promise<DashboardCard[]> {
  await mockDelay(220)
  return [
    { id: 'c1', title: 'Active Mappings', value: '84', trend: '+6%' },
    { id: 'c2', title: 'Pending Reviews', value: '12', trend: '-2%' },
    { id: 'c3', title: 'AI Exports', value: '33', trend: '+11%' },
    { id: 'c4', title: 'Failed Jobs', value: '4', trend: '-1%' },
  ]
}

export async function fetchAdvisoryMappings(filters: { name?: string; status?: string } = {}) {
  await mockDelay(280)
  let out = [...mappingRows]
  if (filters.name?.trim()) {
    const q = filters.name.trim().toLowerCase()
    out = out.filter((r) => r.name.toLowerCase().includes(q))
  }
  if (filters.status && filters.status !== 'all') out = out.filter((r) => r.status === filters.status)
  return out
}

export async function fetchSurveyForms(filters: { name?: string; status?: string } = {}) {
  await mockDelay(260)
  let out = [...surveyRows]
  if (filters.name?.trim()) {
    const q = filters.name.trim().toLowerCase()
    out = out.filter((r) => r.name.toLowerCase().includes(q))
  }
  if (filters.status && filters.status !== 'all') out = out.filter((r) => r.status === filters.status)
  return out
}

export async function fetchAdvisoryAiOrgs(filters: AdvisoryAiOrgFilters): Promise<AdvisoryAiOrgRow[]> {
  const payload = await apiRequest<Record<string, unknown>>('platform/ui/list/grid', {
    method: 'GET',
    query: {
      clientId: filters.clientId,
      filterByCompanyId: true,
    },
  })

  const rows = asArray<Record<string, unknown>>(payload['platforms']).map(mapPlatformGridRowToAdvisoryAiOrg)

  return rows.filter((row) => {
    if (filters.name?.trim()) {
      const query = filters.name.trim().toLowerCase()
      const match = row.name.toLowerCase().includes(query)
      if (!match) return false
    }

    if (filters.companyName?.trim()) {
      const query = filters.companyName.trim().toLowerCase()
      const match = row.companyName.toLowerCase().includes(query)
      if (!match) return false
    }

    if (filters.typeId && Number(filters.typeId) > 0 && row.typeId !== Number(filters.typeId)) {
      return false
    }

    return true
  })
}

export async function fetchAdvisoryAiOrgDetails(orgId: string, clientId: string): Promise<AdvisoryAiOrgProfile | null> {
  const payload = await apiRequest<Record<string, unknown>>('bas/ui/detail', {
    method: 'GET',
    query: { companyId: orgId, clientId },
  })

  const platform = (payload['platform'] ?? payload['Platform']) as Record<string, unknown> | undefined
  if (!platform) return null

  return {
    companyId:
      pickString(platform['companyId']) || pickString(platform['CompanyId']) || pickString(platform['id']) || orgId,
    name:
      pickString(platform['name']) || pickString(platform['Name']) || pickString(platform['companyName']) || 'Unknown Organization',
    companyName:
      pickString(platform['companyName']) || pickString(platform['CompanyName']) || pickString(platform['name']) || 'Unknown Organization',
    profile: pickString(platform['profile']) || pickString(platform['Profile']),
    internalMemory: pickString(platform['internalMemory']) || pickString(platform['InternalMemory']),
  }
}

export async function updateAdvisoryAiOrgProfile(input: UpdateAdvisoryAiOrgProfileInput) {
  return apiRequest<Record<string, unknown>>('bas/ui/edit/profile', {
    method: 'POST',
    query: { clientId: input.clientId },
    body: {
      companyId: input.companyId,
      profile: input.profile,
      internalMemory: input.internalMemory,
    },
  })
}

export async function fetchAdvisoryAiOrgInternalMemory(orgId: string, clientId: string): Promise<AdvisoryAiOrgInternalMemory> {
  const payload = await apiRequest<Record<string, unknown>>(`advisoryai/org/${orgId}/internal-memory`, {
    method: 'GET',
    query: { clientId },
  })

  return {
    orgId: pickString(payload['orgId'] ?? payload['OrgId']) || orgId,
    internalMemory: pickString(payload['internalMemory'] ?? payload['InternalMemory']),
  }
}

export async function updateAdvisoryAiOrgInternalMemory(input: UpdateAdvisoryAiOrgInternalMemoryInput): Promise<AdvisoryAiOrgInternalMemory> {
  const payload = await apiRequest<Record<string, unknown>>(`advisoryai/org/${input.orgId}/internal-memory`, {
    method: 'PUT',
    query: { clientId: input.clientId },
    body: {
      internalMemory: input.internalMemory,
    },
  })

  return {
    orgId: pickString(payload['orgId'] ?? payload['OrgId']) || input.orgId,
    internalMemory: pickString(payload['internalMemory'] ?? payload['InternalMemory']),
  }
}

export async function fetchAdvisoryInternalMemorySuggestion(
  clientId: string,
  orgId: string,
  meetingDate: string
): Promise<{ advisoryCallId: string; meetingDate: string; internalMemory: string }> {
  const payload = await apiRequest<Record<string, unknown>>(
    'advisoryai/internalMemory',
    {
      method: 'GET',
      query: { clientId, orgId, meetingDate },
    }
  )
  return {
    advisoryCallId: pickString(payload['advisoryCallId'] ?? payload['AdvisoryCallId']),
    meetingDate: pickString(payload['meetingDate'] ?? payload['MeetingDate']),
    internalMemory: pickString(payload['internalMemory'] ?? payload['InternalMemory']),
  }
}

export async function createAdvisoryReport(
  input: CreateAdvisoryReportInput
): Promise<CreateAdvisoryReportResponse> {
  const payload = await apiRequest<Record<string, unknown>>('advisoryai/report/create', {
    method: 'POST',
    query: { clientId: input.clientId },
    body: {
      OrgId: input.orgId,
    },
  })

  return {
    advisoryCallId:
      pickString(payload['advisoryCallId']) || pickString(payload['AdvisoryCallId']) || pickString(payload['advisoryReportId']) || pickString(payload['AdvisoryReportId']) || '',
    orgId: pickString(payload['orgId']) || pickString(payload['OrgId']) || input.orgId,
    status: pickString(payload['status']) || pickString(payload['Status']) || 'DRAFT',
    createdAt: pickString(payload['createdAt']) || pickString(payload['CreatedAt']) || '',
  }
}

export async function createConsolidatedReport(
  input: { orgIds: string[]; clientId: string; title: string }
): Promise<{ advisoryCallId: string }> {
  // The create endpoint takes a primary OrgId plus the remaining orgs in OrgIds
  // (disjoint). e.g. { OrgId: A, OrgIds: [B, C] } for a 3-company consolidated analysis.
  const [primaryOrgId, ...otherOrgIds] = input.orgIds
  const payload = await apiRequest<Record<string, unknown>>('advisoryai/report/create', {
    method: 'POST',
    query: { clientId: input.clientId },
    body: {
      OrgId: primaryOrgId,
      OrgIds: otherOrgIds,
      MeetingTitle: input.title,
    },
  })

  return {
    advisoryCallId:
      pickString(payload['advisoryCallId']) ||
      pickString(payload['AdvisoryCallId']) ||
      pickString(payload['advisoryReportId']) ||
      pickString(payload['AdvisoryReportId']) ||
      '',
  }
}

// A consolidated analysis is one advisoryCall spanning >= 2 orgs. The backend returns
// the full AdvisoryCall entity (same shape as a report detail) plus the member orgIds.
// Org names are not included — resolve them client-side via fetchAdvisoryAiOrgs.
export type ConsolidatedAnalysisRow = AdvisoryAiReportDetail & {
  orgId: string
  orgIds: string[]
  /** Normalized access flag. Missing API metadata is treated as allowed for deploy compatibility. */
  canInteract: boolean
}

export async function fetchConsolidatedAnalyses(clientId: string): Promise<ConsolidatedAnalysisRow[]> {
  const payload = await apiRequest<Array<Record<string, unknown>>>(
    `advisoryai/client/${clientId}/consolidated`,
    { method: 'GET' }
  )

  return (payload ?? []).map((item) => ({
    ...(item as unknown as AdvisoryAiReportDetail),
    orgId: pickString(item['orgId'] ?? item['OrgId']),
    orgIds: asArray<unknown>(item['orgIds'] ?? item['OrgIds'])
      .map((value) => pickString(value))
      .filter(Boolean),
    revaluationMissing: pickBool(item['revaluationMissing'] ?? item['RevaluationMissing']),
    canInteract: pickBool(item['canInteract'] ?? item['CanInteract'], true),
  }))
}

export async function generateAdvisoryAiReport(
  input: GenerateAdvisoryAiReportInput
): Promise<GenerateAdvisoryAiReportResponse> {
  const payload = await apiRequest<Record<string, unknown>>('advisoryai/report', {
    method: 'POST',
    query: { clientId: input.clientId },
    body: {
      orgId: input.orgId,
      startDate: formatDateForApi(input.startDate),
      endDate: formatDateForApi(input.endDate),
      reportTypes: input.reportTypes,
    },
  })

  return {
    advisoryCallId:
      pickString(payload['advisoryCallId']) || pickString(payload['AdvisoryCallId']) || pickString(payload['advisoryReportId']) || pickString(payload['AdvisoryReportId']),
  }
}

export async function fetchAdvisoryAiReport(reportId: string, clientId: string): Promise<AdvisoryAiReportDetail> {
  if (isSampleCall(reportId)) return structuredClone(SAMPLE_REPORT)

  const payload = await apiRequest<Record<string, unknown>>(`advisoryai/report/${reportId}`, {
    method: 'GET',
    query: { clientId },
  })

  return {
    ...(payload as unknown as AdvisoryAiReportDetail),
    revaluationMissing: pickBool(payload?.['revaluationMissing'] ?? payload?.['RevaluationMissing']),
  }
}

export async function exportAdvisoryAiReport(
  reportId: string,
  clientId: string,
  exportFormat: AdvisoryAiExportFormat,
  // Which voice to use for the video export. null/undefined = the system default voice;
  // otherwise an advisoryVoiceCloneId. Ignored by the backend for non-video formats.
  voiceCloneId?: string | null
): Promise<AdvisoryAiExportEnqueueResponse> {
  if (isSampleCall(reportId)) throw new SampleCallError()

  const payload = await apiRequest<Record<string, unknown>>('advisoryai/report/export', {
    method: 'POST',
    query: { clientId },
    body: {
      reportId,
      exportFormat,
      ...(exportFormat === 'video' ? { voiceCloneId: voiceCloneId ?? null } : {}),
    },
  })
  return {
    reportId: pickString(payload['reportId'] ?? payload['ReportId']) || reportId,
    exportFormat: (pickString(payload['exportFormat'] ?? payload['ExportFormat']) || exportFormat) as AdvisoryAiExportFormat,
    status: pickString(payload['status'] ?? payload['Status']) || 'QUEUED',
  }
}

export async function fetchAdvisoryAiOrgReports(
  orgId: string,
  pageIndex: number,
  pageSize: number,
  clientId: string
): Promise<AdvisoryAiOrgReportsResponse> {
  const payload = await apiRequest<Array<Record<string, unknown>>>(`advisoryai/${orgId}/reports`, {
    method: 'GET',
    query: {
      clientId,
      orgId,
      skip: Math.max(pageIndex - 1, 0) * pageSize,
      take: pageSize,
    },
  })

  const rows = (payload ?? []).map((item) => ({
    ...(item as unknown as AdvisoryAiOrgReportRow),
    reports: asArray<unknown>(item['reports'] ?? item['Reports']).filter(
      (report): report is string => typeof report === 'string'
    ),
    revaluationMissing: pickBool(item['revaluationMissing'] ?? item['RevaluationMissing']),
    // The API remains the authorization boundary. Default to allowed so an older response that
    // omits this UI hint does not make every meeting unusable during a skewed deployment.
    canInteract: pickBool(item['canInteract'] ?? item['CanInteract'], true),
  }))

  return {
    rows,
    totalCount: rows.length,
  }
}

export async function downloadAdvisoryAiReport(reportId: string, clientId: string, exportType: string) {
  if (isSampleCall(reportId)) throw new SampleCallError()

  const payload = await apiRequest<Record<string, unknown>>('advisoryai/report/download', {
    method: 'GET',
    query: {
      reportId,
      clientId,
      exportType,
    },
  })
  return {
    url: pickString(payload['url'] ?? payload['Url']),
  }
}

export function getAdvisoryAiReportTypeDisplayName(reportType: string): string {
  switch (reportType) {
    case 'profitability':
      return 'Profitability Analysis'
    case 'accounts_receivable':
      return 'Accounts Receivable'
    case 'accounts_payable':
      return 'Accounts Payable'
    case 'balance_sheet':
      return 'Balance Sheet'
    case 'cashflow':
      return 'Cashflow'
    default:
      return reportType
  }
}

export const ADVISORY_AI_REPORT_TYPES: Array<{ value: AdvisoryAiReportType; label: string }> = [
  { value: 'profitability', label: 'Profitability' },
  { value: 'accounts_receivable', label: 'Accounts Receivable' },
  { value: 'accounts_payable', label: 'Accounts Payable' },
  { value: 'balance_sheet', label: 'Balance Sheet' },
  { value: 'cashflow', label: 'Cashflow' },
]

export const ADVISORY_AI_EXPORT_FORMATS: Array<{ value: AdvisoryAiExportFormat; label: string; help: string }> = [
  { value: 'pdf', label: 'PDF', help: 'Export the AI insights in full.' },
  { value: 'pdf-summary', label: 'PDF Summary', help: 'Export a condensed summary of the insights.' },
  { value: 'ppt', label: 'PPT', help: 'Compile the insights into a slideshow.' },
  { value: 'email', label: 'Email', help: 'Create an email draft with summary attachment.' },
  { value: 'video', label: 'Video', help: 'Compile insights into a narrated presentation video.' },
]

function mapAdvisoryAgendaTemplateLine(row: Record<string, unknown>): AdvisoryAgendaTemplate['items'][number] {
  return {
    title: pickString(row['title']) || pickString(row['Title']) || '',
    description: pickString(row['description']) || pickString(row['Description']) || '',
  }
}

function mapAdvisoryAgendaTemplate(row: Record<string, unknown>): AdvisoryAgendaTemplate {
  const rawItems = row['items'] ?? row['Items']
  const items = Array.isArray(rawItems)
    ? (rawItems as unknown[]).map((entry) => mapAdvisoryAgendaTemplateLine(entry as Record<string, unknown>))
    : []
  return {
    advisoryAgendaTemplateId: pickString(row['advisoryAgendaTemplateId']) || pickString(row['AdvisoryAgendaTemplateId']) || '',
    clientId: pickString(row['clientId']) || pickString(row['ClientId']) || '',
    title: pickString(row['title']) || pickString(row['Title']) || '',
    items,
    createdAt: parseDate(row['createdAt'] ?? row['CreatedAt']),
    updatedAt: parseDate(row['updatedAt'] ?? row['UpdatedAt']),
    createdBy: pickString(row['createdBy'] ?? row['CreatedBy']) || undefined,
    updatedBy: pickString(row['updatedBy'] ?? row['UpdatedBy']) || undefined,
  }
}

function unwrapAgendaTemplateResponse(payload: Record<string, unknown>): Record<string, unknown> {
  const inner = payload['data'] ?? payload['Data']
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return payload
}

export async function fetchAdvisoryAgendaTemplates(clientId: string): Promise<AdvisoryAgendaTemplate[]> {
  const payload = await apiRequest<Record<string, unknown>>(`advisoryai/client/${clientId}/agenda-templates`, {
    method: 'GET',
  })

  const items = asArray<Record<string, unknown>>(payload['data'] ?? payload['Data'] ?? payload).map(mapAdvisoryAgendaTemplate)
  return items
}

export async function createAdvisoryAgendaTemplate(input: CreateAdvisoryAgendaTemplateInput): Promise<AdvisoryAgendaTemplate> {
  const payload = await apiRequest<Record<string, unknown>>(`advisoryai/client/${input.clientId}/agenda-templates`, {
    method: 'POST',
    body: {
      title: input.title,
      items: input.items ?? [],
    },
  })

  return mapAdvisoryAgendaTemplate(unwrapAgendaTemplateResponse(payload))
}

export async function updateAdvisoryAgendaTemplate(input: UpdateAdvisoryAgendaTemplateInput): Promise<AdvisoryAgendaTemplate> {
  const payload = await apiRequest<Record<string, unknown>>(`advisoryai/client/${input.clientId}/agenda-templates/${input.templateId}`, {
    method: 'PUT',
    body: {
      title: input.title,
      items: input.items ?? [],
    },
  })

  return mapAdvisoryAgendaTemplate(unwrapAgendaTemplateResponse(payload))
}

export async function deleteAdvisoryAgendaTemplate(templateId: string, clientId: string): Promise<void> {
  await apiRequest<Record<string, unknown>>(`advisoryai/client/${clientId}/agenda-templates/${templateId}`, {
    method: 'DELETE',
  })
}

export async function analyzeAdvisoryReport(input: AnalyzeAdvisoryReportInput): Promise<void> {
  if (isSampleCall(input.reportId)) throw new SampleCallError()

  await apiRequest(`advisoryai/report/${input.reportId}/analyze`, {
    method: 'POST',
    query: { clientId: input.clientId },
    body: {
      ReportTypes: input.reportTypes,
    },
  })
}

export async function updateAdvisoryCall(input: UpdateAdvisoryCallInput): Promise<AdvisoryAiReportDetail> {
  if (isSampleCall(input.reportId)) throw new SampleCallError()

  return apiRequest<AdvisoryAiReportDetail>(`advisoryai/report/${input.reportId}`, {
    method: 'PUT',
    query: { clientId: input.clientId },
    body: {
      Summary: input.summary,
      Notes: input.notes,
      InternalMemory: input.internalMemory,
      MeetingTitle: input.meetingTitle,
      Status: input.status,
      StartDate: input.startDate,
      EndDate: input.endDate,
      MeetingDate: input.meetingDate,
      Reports: input.reports,
      AccountMapping: input.accountMapping,
      IncludeHeadlinerSummary: input.includeHeadlinerSummary,
      AgendaItems: input.agendaItems?.map((item) => ({
        AdvisoryAgendaId: item.advisoryAgendaId,
        Title: item.title,
        Description: item.description,
      })),
    },
  })
}

export async function batchUpdateFindings(input: BatchUpdateFindingsInput): Promise<void> {
  if (isSampleCall(input.reportId)) throw new SampleCallError()

  await apiRequest<Record<string, unknown>>(
    `advisoryai/report/${input.reportId}/findings/batch`,
    {
      method: 'PUT',
      query: { clientId: input.clientId },
      body: {
        AdvisoryAgendaId: input.advisoryAgendaId,
        Created: input.created.map((c) => ({
          Type: c.type,
          Heading: c.heading ?? null,
          Description: c.description,
          Hidden: c.hidden ?? false,
        })),
        Updated: input.updated.map((u) => ({
          AdvisoryFindingId: u.advisoryFindingId,
          Type: u.type,
          Heading: u.heading ?? null,
          Description: u.description,
          Hidden: u.hidden ?? false,
        })),
        Deleted: input.deleted,
      },
    }
  )
}

export async function deleteAdvisoryCall(reportId: string, clientId: string): Promise<void> {
  if (isSampleCall(reportId)) throw new SampleCallError()

  await apiRequest<Record<string, unknown>>(`advisoryai/report/${reportId}`, {
    method: 'DELETE',
    query: { clientId },
  })
}

export async function fetchAdvisoryExports(
  reportId: string,
  clientId: string
): Promise<AdvisoryExport[]> {
  if (isSampleCall(reportId)) return structuredClone(SAMPLE_EXPORTS)

  const payload = await apiRequest<Array<Record<string, unknown>>>(`advisoryai/report/${reportId}/exports`, {
    method: 'GET',
    query: { clientId },
  })

  const idless: string[] = []

  const exports = (payload || []).map((item) => {
    const advisoryExportId = pickString(item['advisoryExportId'] ?? item['AdvisoryExportId'] ?? '')
    if (!advisoryExportId) idless.push(pickString(item['exportType'] ?? item['ExportType']) || 'unknown')

    return {
      advisoryExportId,
      advisoryCallId: pickString(item['advisoryCallId'] ?? item['AdvisoryCallId'] ?? ''),
      exportType: (pickString(item['exportType'] ?? item['ExportType']) || 'pdf') as AdvisoryAiExportFormat,
      status: (pickString(item['status'] ?? item['Status']) || 'PENDING_EXPORT') as AdvisoryAiExportStatus,
      url: pickString(item['url'] ?? item['Url']) || null,
      fileSize: pickNumber(item['fileSize'] ?? item['FileSize']) || null,
      pageCount: pickNumber(item['pageCount'] ?? item['PageCount']) || null,
      createdAt: pickString(item['createdAt'] ?? item['CreatedAt'] ?? ''),
      updatedAt: pickString(item['updatedAt'] ?? item['UpdatedAt'] ?? ''),
      contentUpdatedAt:
        pickString(item['contentUpdatedAt'] ?? item['ContentUpdatedAt']) || null,
      isOutdated: Boolean(item['isOutdated'] ?? item['IsOutdated']),
    }
  })

  // One line per response, not per row: this list polls every 3s while an export
  // is running, and a row without an id cannot be reported as downloaded — so
  // the '' fallback above would otherwise make that failure silent.
  if (idless.length) {
    console.warn('[advisory-exports] export rows arrived without an id', { exportTypes: idless })
  }

  return exports
}

export async function downloadAdvisoryExportsZip(
  reportId: string,
  clientId: string,
  exportTypes: AdvisoryAiExportFormat[]
) {
  if (isSampleCall(reportId)) throw new SampleCallError()

  return apiRequestBlob(`advisoryai/report/${reportId}/exports/download`, {
    method: 'POST',
    query: { clientId },
    body: { exportTypes },
  })
}

/**
 * Tells the server a download actually happened, which is what the export
 * pop-ups are written from.
 *
 * 204 with an empty body — it deliberately says nothing about which pop-ups it
 * wrote, so the caller reads the feed back instead of reading a response.
 * Sample calls have no server-side exports, so there is nothing to report.
 *
 * Returns whether a request was actually sent, so the caller does not read the
 * feed back — a GET that records an AdvicePrep visit — after a no-op.
 */
export async function markAdvisoryExportsDownloaded(
  reportId: string,
  clientId: string,
  exportIds: string[]
): Promise<boolean> {
  if (isSampleCall(reportId) || exportIds.length === 0) return false

  await apiRequest<void>(`advisoryai/report/${reportId}/exports/downloaded`, {
    method: 'POST',
    query: { clientId },
    body: { exportIds },
  })
  return true
}

export async function fetchAdvisoryAiThemeColors(clientId: string): Promise<AdvisoryAiThemeColors> {
  const payload = await apiRequest<Record<string, unknown>>(`advisoryai/client/${clientId}/theme-colors`, {
    method: 'GET',
  })

  return {
    clientId: pickString(payload['clientId'] ?? payload['ClientId']) || clientId,
    primaryColor: pickString(payload['primaryColor'] ?? payload['PrimaryColor']),
    secondaryColor: pickString(payload['secondaryColor'] ?? payload['SecondaryColor']),
    textColor: pickString(payload['textColor'] ?? payload['TextColor']),
  }
}

export async function updateAdvisoryAiThemeColors(
  input: UpdateAdvisoryAiThemeColorsInput
): Promise<AdvisoryAiThemeColors> {
  const payload = await apiRequest<Record<string, unknown>>(`advisoryai/client/${input.clientId}/theme-colors`, {
    method: 'POST',
    body: {
      primaryColor: input.primaryColor,
      secondaryColor: input.secondaryColor,
      textColor: input.textColor,
    },
  })

  return {
    clientId: pickString(payload['clientId'] ?? payload['ClientId']) || input.clientId,
    primaryColor: pickString(payload['primaryColor'] ?? payload['PrimaryColor']),
    secondaryColor: pickString(payload['secondaryColor'] ?? payload['SecondaryColor']),
    textColor: pickString(payload['textColor'] ?? payload['TextColor']),
  }
}

export async function sendDeepDiveMessage(input: DeepDiveChatRequest): Promise<DeepDiveChatResponse> {
  if (isSampleCall(input.advisoryCallId)) throw new SampleCallError()

  return apiRequest<DeepDiveChatResponse>('advisoryai/deepDive/message', {
    method: 'POST',
    query: { clientId: input.clientId },
    body: {
      advisoryCallId: input.advisoryCallId,
      clientId: input.clientId,
      question: input.question,
      messages: input.messages,
    },
  })
}

export type CloneVoiceInput = {
  // Required when the user has no clone yet; optional when updating (metadata-only update keeps the existing audio).
  file?: File | null
  isMale: boolean
  voiceName: string
}

// GET /api/User/media — combined voice + avatar detail (each with a presigned URL).
// The cheaper GET /api/User/media/status (has/status only, no presign) is also available
// for pure gating, but every screen that gates here also needs the voice detail.
export type UserMedia = {
  voice: AdvisoryVoiceClone | null
  avatar: { hasAvatar: boolean; url: string | null }
}

export type CloneVoiceResult = {
  success: boolean
  advisoryVoiceCloneId: string
  voiceName: string
  isMale: boolean
  status: string
}

export type AdvisoryVoiceClone = {
  advisoryVoiceCloneId: string
  voiceName: string
  isMale: boolean
  status: string
  hasSample: boolean
  sampleUrl: string
  errorMessage: string
  createdAt: string
}

// A clone is still being processed (so we should keep polling) unless it's reached a terminal state.
export function isVoiceCloneProcessing(status?: string): boolean {
  return !!status && /pending|processing|in.?progress|queued|running/i.test(status)
}

function mapVoiceClone(value: unknown): AdvisoryVoiceClone {
  const row = (value ?? {}) as Record<string, unknown>
  return {
    advisoryVoiceCloneId: pickString(
      row['advisoryVoiceCloneId'] ?? row['AdvisoryVoiceCloneId'] ?? row['id'] ?? row['Id']
    ),
    voiceName: pickString(row['voiceName'] ?? row['VoiceName']),
    isMale: Boolean(row['isMale'] ?? row['IsMale']),
    status: pickString(row['status'] ?? row['Status']) || 'Pending',
    hasSample: Boolean(row['hasSample'] ?? row['HasSample']),
    sampleUrl: pickString(row['sampleUrl'] ?? row['SampleUrl']),
    errorMessage: pickString(row['errorMessage'] ?? row['ErrorMessage']),
    createdAt: pickString(row['createdAt'] ?? row['CreatedAt'] ?? row['createdOn'] ?? row['CreatedOn']),
  }
}

// GET /api/User/media — the current user's voice + avatar detail (presigned URLs).
// Missing voice/avatar keys (NullValueHandling.Ignore) are treated as "none".
export async function fetchUserMedia(): Promise<UserMedia> {
  const payload = await apiRequest<Record<string, unknown>>('user/media', {
    method: 'GET',
  })
  const r = payload ?? {}
  const voiceRaw = r['voice'] ?? r['Voice']
  const voice = voiceRaw && typeof voiceRaw === 'object' ? mapVoiceClone(voiceRaw) : null
  const avatarRaw = (r['avatar'] ?? r['Avatar'] ?? {}) as Record<string, unknown>
  return {
    voice: voice && voice.advisoryVoiceCloneId ? voice : null,
    avatar: {
      hasAvatar: Boolean(avatarRaw['hasAvatar'] ?? avatarRaw['HasAvatar']),
      url: pickString(avatarRaw['url'] ?? avatarRaw['Url']) || null,
    },
  }
}

// POST /api/User/media/voice — create or update the current user's voice.
// File is required only when the user has no clone yet.
export async function cloneVoice(input: CloneVoiceInput): Promise<CloneVoiceResult> {
  const formData = new FormData()
  if (input.file) formData.append('file', input.file)
  formData.append('isMale', String(input.isMale))
  formData.append('voiceName', input.voiceName)
  const payload = await apiRequest<Record<string, unknown>>('user/media/voice', {
    method: 'POST',
    formData,
  })
  return {
    success: Boolean(payload['success']),
    advisoryVoiceCloneId: pickString(payload['advisoryVoiceCloneId'] ?? payload['AdvisoryVoiceCloneId']),
    voiceName: pickString(payload['voiceName'] ?? payload['VoiceName']) || input.voiceName,
    isMale: payload['isMale'] === undefined ? input.isMale : Boolean(payload['isMale'] ?? payload['IsMale']),
    status: pickString(payload['status'] ?? payload['Status']) || 'Pending',
  }
}

// GET /api/User/media/voice/{id} — get one voice clone (used for status polling); scoped to current user.
export async function fetchVoiceClone(advisoryVoiceCloneId: string): Promise<AdvisoryVoiceClone | null> {
  const payload = await apiRequest<Record<string, unknown>>(
    `user/media/voice/${advisoryVoiceCloneId}`,
    {
      method: 'GET',
    }
  )
  return payload ? mapVoiceClone(payload) : null
}
